import * as React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RateReviewIcon from "@mui/icons-material/RateReview";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate } from "react-router-dom";
import { Localized, useLocalization } from "@fluent/react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadMyTasks } from "../../app/AppDataLoader";
import { LoadingContainer } from "../../app/LoadingContainer";
import { selectModule } from "../modules/moduleSlice";
import { loadUserTasks, MyTask } from "./myTasksSlice";

import { useEffect, useState } from "react";
import { bucketOf } from "./utils/taskBuckets";
import { TaskWidget } from "./widgets/TaskWidget";
import { DocumentsWidgetTabbed } from "./widgets/DocumentsWidgetTabbed";
import { loadMyDocuments, DocumentWidgetItem } from "./myDocumentsSlice";
import { getLink } from "../documents/components/DocumentLinkProvider";
import { taskTargetLink } from "./utils/taskRelatedEntity";

export const MyTasks: React.FC = () => {
  const [all, setTasks] = useState<MyTask[]>([]);
  const [recentDocs, setRecentDocs] = useState<DocumentWidgetItem[]>([]);
  const [progressDocs, setProgressDocs] = useState<DocumentWidgetItem[]>([]);
  const [waitingDocs, setWaitingDocs] = useState<DocumentWidgetItem[]>([]);

  const currentModule = useAppSelector(state => selectModule(state).currentModule);
  const navigate = useNavigate();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();

  const toApprove = React.useMemo(() => all.filter(x => bucketOf(x.taskType) === "DOCUMENT_APPROVAL"), [all]);
  const toReview = React.useMemo(() => all.filter(x => bucketOf(x.taskType) === "DOCUMENT_OPINION"), [all]);

  useEffect(() => {
    if (currentModule === null) navigate("/modules");
  }, [currentModule, navigate]);

  useEffect(() => {
    fetchUserTasks();
    fetchMyDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserTasks = async () => {
    try {
      const userTasks = await dispatch(loadUserTasks()).unwrap();
      setTasks(userTasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const fetchMyDocs = async () => {
    try {
      const data = await dispatch(loadMyDocuments()).unwrap();
      setRecentDocs(data.recent ?? []);
      setProgressDocs(data.inProgress ?? []);
      setWaitingDocs(data.waiting ?? []);
    } catch (e) {
      console.error("Error fetching docs:", e);
      setRecentDocs([]); setProgressDocs([]); setWaitingDocs([]);
    }
  };

  const handleGoToDocument = (rawLink: string) => {
    if (!rawLink) return;

    if (/^https?:\/\//i.test(rawLink)) {
      const url = new URL(rawLink);
      if (url.origin === window.location.origin) {
        navigate(url.pathname + url.search + url.hash);
      } else {
        window.open(rawLink, "_blank", "noopener,noreferrer");
      }
      return;
    }
    const path = rawLink.startsWith("/") ? rawLink : `/${rawLink}`;
    navigate(path);
  };

  const goToTask = (row: MyTask) => handleGoToDocument(taskTargetLink(row));
  const openDoc = (d: DocumentWidgetItem) => handleGoToDocument(getLink(d.type, d.id));

  return (
    <LoadMyTasks component={<LoadingContainer />}>
      <Stack
        spacing={2}
        sx={{
          minHeight: { xs: "auto", md: "calc(100vh - 160px)" },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <DashboardIcon color="primary" />
          <Typography variant="h5" fontWeight={800}>
            <Localized id="menu-my-tasks"><span>_my tasks</span></Localized>
          </Typography>
        </Stack>

        <Grid
          container
          spacing={2}
          alignItems="stretch"
          sx={{
            flex: { xs: "unset", md: 1 },
            minHeight: { xs: "auto", md: 0 },
          }}
        >
          <Grid item xs={12} md={6} sx={{ display: "flex", minHeight: { xs: "auto", md: 0 } }}>
            <DocumentsWidgetTabbed
              title={l10n.getString("my-documents", null, "My documents")}
              icon={<DescriptionIcon color="primary" />}
              recent={recentDocs}
              progress={progressDocs}
              waiting={waitingDocs}
              onOpen={openDoc}
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: "flex", minHeight: 0 }}>
            <Stack spacing={2} sx={{ height: "100%", width: "100%", minHeight: 0 }}>
              <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
                <TaskWidget
                  title={l10n.getString("task-documents-to-approve", null, "Dokumenty do zatwierdzenia")}
                  badgeColor="success.main"
                  icon={<TaskAltIcon color="success" />}
                  items={toApprove}
                  onOpen={goToTask}
                />
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
                <TaskWidget
                  title={l10n.getString("task-documents-to-review", null, "Dokumenty do zaopiniowania")}
                  badgeColor="info.main"
                  icon={<RateReviewIcon color="info" />}
                  items={toReview}
                  onOpen={goToTask}
                />
              </Box>
            </Stack>

          </Grid>

        </Grid>
      </Stack>
    </LoadMyTasks>
  );
};
